import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Sparkles, CalendarDays, Store, IdCard, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

function money(value, currency = 'UGX') {
  return `${currency} ${Number(value || 0).toLocaleString()}`;
}

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

export default function PackagePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingCheckout, setPendingCheckout] = useState('');

  useEffect(() => {
    api.get('/dashboard/packages').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-48"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const current = data?.current_package || data?.access?.package || null;
  const daysLeft = data?.access?.days_left;
  const access = data?.access || {};

  const accessItems = [
    ['Store', access.can_create_store],
    ['vCard', access.can_create_vcard],
    ['Invoices', access.can_create_invoices],
    ['Quotations', access.can_create_quotations],
    ['CRM', access.can_use_crm],
    ['Reminders', access.can_use_payment_reminders],
    ['Subscriptions', access.can_use_subscriptions],
    ['Budget', access.can_use_budget],
    ['Business Profile', access.can_use_business_profile],
    ['QR Codes', access.can_use_qr_codes],
    ['POS', access.can_use_pos],
    ['Receipts', access.can_use_receipts],
    ['Delivery Options', access.can_use_delivery_options],
    ['Analytics', access.can_use_analytics],
    ['Staff', access.can_use_staff_accounts],
    ['WhatsApp Templates', access.can_use_whatsapp_templates],
    ['Bulk Payment Links', access.can_use_bulk_payment_links],
    ['Marketplace', access.can_use_marketplace],
    ['Custom Domains', access.custom_domain_allowed],
    ['Remove Branding', access.remove_branding_allowed],
  ];

  const startCheckout = async (pkg, billingCycle = 'monthly') => {
    setPendingCheckout(pkg.id || pkg.code || '');
    try {
      const res = await api.post('/dashboard/packages/checkout', {
        package_id: pkg.id || pkg.code,
        billing_cycle: billingCycle,
      });
      const checkoutUrl = res.data?.checkout_url;
      if (!checkoutUrl) {
        throw new Error('Checkout URL not returned');
      }
      window.location.href = checkoutUrl;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start upgrade checkout');
      setPendingCheckout('');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>My Package / Subscription</h1>
        <p className="text-slate-500 text-sm mt-0.5">Manage your store and vCard access from one subscription plan.</p>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Current Subscription</CardTitle>
          <Badge className="bg-emerald-100 text-emerald-800">{data?.access?.active ? 'Active' : 'Inactive'}</Badge>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
            <p className="text-xs text-slate-500">Package</p>
            <p className="mt-1 font-semibold text-slate-900">{current?.name || 'Free Trial'}</p>
            <p className="text-xs text-slate-500 mt-1">Code: {current?.code || 'trial'}</p>
            <p className="text-xs text-slate-500 mt-1">Currency: {current?.currency || 'UGX'}</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
            <p className="text-xs text-slate-500">Trial / Subscription</p>
            <p className="mt-1 font-semibold text-slate-900">{daysLeft != null ? `${daysLeft} day(s) left` : 'No active trial'}</p>
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><CalendarDays size={12} /> Manage renewal from admin or support.</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
            <p className="text-xs text-slate-500">Product Limit</p>
            <p className="mt-1 font-semibold text-slate-900">{data?.access?.product_limit || 0} products</p>
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Store size={12} /> Upgrade to unlock more products.</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
            <p className="text-xs text-slate-500">Branding</p>
            <p className="mt-1 font-semibold text-slate-900">{data?.access?.remove_branding_allowed ? 'Can remove branding' : 'Branding required'}</p>
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><ShieldCheck size={12} /> Feature controlled by package.</p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Feature Access</CardTitle>
          {!access?.active && (
            <Button
              type="button"
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              onClick={async () => {
                try {
                  const res = await api.post('/dashboard/package/trial');
                  toast.success(res.data?.message || 'Trial started');
                  const next = await api.get('/dashboard/packages');
                  setData(next.data);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Could not start trial');
                }
              }}
            >
              Start Trial
            </Button>
          )}
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {accessItems.map(([label, value]) => (
            <div key={label} className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
              <span className="text-sm text-slate-700">{label}</span>
              <Badge className={value ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}>
                {value ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {(data?.packages || []).map((pkg) => (
          <Card key={pkg.id || pkg.code} className={`bg-white border shadow-sm rounded-2xl ${current?.id === pkg.id ? 'border-emerald-300' : 'border-slate-200'}`}>
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{pkg.name}</CardTitle>
              <p className="text-sm text-slate-500">{pkg.priority_support_label || 'Standard support'}</p>
              {pkg.description ? <p className="text-xs text-slate-500">{pkg.description}</p> : null}
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-baseline gap-2">
                <p className="text-2xl font-semibold text-slate-900">{money(pkg.monthly_price, pkg.currency || 'UGX')}</p>
                <span className="text-xs text-slate-500">monthly</span>
              </div>
              <p className="text-sm text-slate-500">{money(pkg.yearly_price, pkg.currency || 'UGX')} yearly</p>
              <div className="space-y-1 text-sm text-slate-600">
                <p><strong>Store:</strong> {pkg.can_create_store ? 'Yes' : 'No'}</p>
                <p><strong>vCard:</strong> {pkg.can_create_vcard ? 'Yes' : 'No'}</p>
                <p><strong>Product limit:</strong> {pkg.product_limit || 0}</p>
                <p><strong>Custom domains:</strong> {pkg.custom_domain_allowed ? 'Yes' : 'No'}</p>
                <p><strong>Remove branding:</strong> {pkg.remove_branding_allowed ? 'Yes' : 'No'}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  disabled={pendingCheckout === (pkg.id || pkg.code || '')}
                  onClick={() => startCheckout(pkg, 'monthly')}
                >
                  <Sparkles size={14} className="mr-1" /> {pendingCheckout === (pkg.id || pkg.code || '') ? 'Opening...' : 'Monthly'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  disabled={pendingCheckout === (pkg.id || pkg.code || '')}
                  onClick={() => startCheckout(pkg, 'yearly')}
                >
                  Yearly
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Package History</CardTitle>
          <Badge variant="outline" className="border-slate-200 text-slate-600 bg-slate-50">
            {Array.isArray(data?.history) ? data.history.length : 0} record(s)
          </Badge>
        </CardHeader>
        <CardContent>
          {Array.isArray(data?.history) && data.history.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-100">
                    <th className="py-3 pr-4 font-medium">Package</th>
                    <th className="py-3 pr-4 font-medium">Cycle</th>
                    <th className="py-3 pr-4 font-medium">Status</th>
                    <th className="py-3 pr-4 font-medium">Amount</th>
                    <th className="py-3 pr-4 font-medium">Reference</th>
                    <th className="py-3 pr-4 font-medium">Period</th>
                    <th className="py-3 pr-4 font-medium">Receipt Email</th>
                    <th className="py-3 pr-4 font-medium">Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {data.history.map((item, index) => {
                    const pkg = item?.package || {};
                    const sub = item?.subscription || {};
                    const txn = item?.transaction || {};
                    const payment = item?.package_payment || {};
                    const ref = txn.reference_code || sub.payment_transaction_reference || '';
                    const receiptEmailSent = Boolean(txn.package_receipt_email_sent_at || sub.receipt_email_sent_at || payment.email_sent_at);
                    return (
                      <tr key={`${ref || index}`} className="border-b border-slate-100 last:border-b-0">
                        <td className="py-3 pr-4">
                          <div className="font-medium text-slate-900">{pkg.name || 'Subscription'}</div>
                          <div className="text-xs text-slate-500">{pkg.priority_support_label || 'Standard support'}</div>
                        </td>
                        <td className="py-3 pr-4 uppercase text-slate-600">{sub.billing_cycle || 'monthly'}</td>
                        <td className="py-3 pr-4">
                          <Badge className={`${(sub.status || txn.status || '').toLowerCase() === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}`}>
                            {(sub.status || payment.status || txn.status || 'pending').replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="py-3 pr-4 text-slate-700">
                          {payment.amount != null ? money(payment.amount, payment.currency || 'UGX') : txn.amount != null ? money(txn.amount, txn.currency || 'UGX') : money(pkg[sub.billing_cycle === 'yearly' ? 'yearly_price' : 'monthly_price'], pkg.currency || 'UGX')}
                        </td>
                        <td className="py-3 pr-4 text-slate-600 break-all">{ref || '-'}</td>
                        <td className="py-3 pr-4 text-slate-600">
                          <div>{formatDate(sub.subscription_start || txn.created_at)}</div>
                          <div className="text-xs text-slate-500">to {formatDate(sub.subscription_end)}</div>
                        </td>
                        <td className="py-3 pr-4">
                          <Badge className={receiptEmailSent ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}>
                            {receiptEmailSent ? 'Sent' : 'Not sent'}
                          </Badge>
                        </td>
                        <td className="py-3 pr-4">
                          {ref ? (
                            <Link to={`/receipt/package?ref=${encodeURIComponent(ref)}`} className="text-emerald-700 hover:text-emerald-800 font-medium">
                              View receipt
                            </Link>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
              No package purchases yet. Your subscription history will appear here after an upgrade.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
